<template>
  <section class="il-wrapper" v-loading="loading">
    <!-- KPI summary -->
    <div
      class="il-summary"
      key="il-summary"
      v-if="loading || resultQueryInvoices.length"
    >
      <div class="il-summary__card">
        <span class="il-summary__label">Ventas brutas</span>
        <span v-if="loading" class="il-summary__skeleton"></span>
        <span v-else class="il-summary__value">
          <FormatAmount :amount="summarySubtotal" />
        </span>
      </div>
      <div class="il-summary__card">
        <span class="il-summary__label">IVA total</span>
        <span v-if="loading" class="il-summary__skeleton"></span>
        <span v-else class="il-summary__value">
          <FormatAmount :amount="summaryIva" />
        </span>
      </div>
      <div class="il-summary__card">
        <span class="il-summary__label">Total facturado</span>
        <span v-if="loading" class="il-summary__skeleton"></span>
        <span v-else class="il-summary__value il-summary__value--accent">
          <FormatAmount :amount="summaryTotal" />
        </span>
      </div>
      <div class="il-summary__card">
        <span class="il-summary__label">Facturas emitidas</span>
        <span v-if="loading" class="il-summary__skeleton"></span>
        <span v-else class="il-summary__value">{{
          resultQueryInvoices.length
        }}</span>
      </div>
    </div>

    <!-- Toolbar filtros -->
    <div class="dir-toolbar" key="il-toolbar">
      <div class="dir-toolbar-search">
        <InputSearchComponent @onChangeSearch="onChangeSearch" />
      </div>
      <FilterBetweenDatesSelect
        :defaultMonthValue="true"
        @onAsyncDataForDates="onAsyncDataForDates"
      />
      <div class="dir-toolbar-right">
        <template v-if="resultQueryInvoices.length">
          <ExportButtons
            :data="resultQueryInvoices"
            :columns="exportColumns"
            filename="reporte_facturas"
          />
          <el-tooltip content="Descargar PDF" placement="top">
            <button class="dir-view-toggle" @click="sendReport">
              <i class="el-icon-download"></i>
            </button>
          </el-tooltip>
        </template>
        <span id="url_download"></span>
      </div>
    </div>

    <!-- Empty state -->
    <div
      v-if="!loading && !resultQueryInvoices.length"
      key="il-empty"
      class="il-empty"
    >
      <i class="el-icon-document"></i>
      <p>No hay facturas para el período seleccionado</p>
    </div>

    <template v-if="resultQueryInvoices.length">
      <!-- ── DESKTOP: cards ── -->
      <div class="d-none d-md-block" key="il-desktop">
        <div class="table-items-header il-header">
          <b-row>
            <b-col md="2"><h6 class="h6 ml-2">Factura</h6></b-col>
            <b-col md="2"><h6 class="h6">RUC / CI</h6></b-col>
            <b-col md="2"><h6 class="h6">Cliente</h6></b-col>
            <b-col md="1"><h6 class="h6">Fecha</h6></b-col>
            <b-col md="1"><h6 class="h6 text-center">Items</h6></b-col>
            <b-col md="1"><h6 class="h6 text-right">Subtotal</h6></b-col>
            <b-col md="1"><h6 class="h6 text-right">IVA</h6></b-col>
            <b-col md="2"><h6 class="h6 text-right">Total</h6></b-col>
          </b-row>
        </div>

        <paginate
          name="resultQueryInvoices"
          :list="resultQueryInvoices"
          :per="perPaginate"
          :key="searchQuery"
        >
          <div
            class="card il-card-row"
            v-for="(invoice, index) in paginated('resultQueryInvoices')"
            :key="index"
            @click="openPreview(invoice)"
          >
            <div class="card-body il-card-row__body">
              <b-row class="align-items-center">
                <b-col md="2">
                  <span class="il-invoice-num">{{
                    invoice.number_invoice
                  }}</span>
                </b-col>
                <b-col md="2">
                  <span class="il-text-mono">{{
                    invoice.client ? invoice.client.document : "—"
                  }}</span>
                </b-col>
                <b-col md="2">
                  <span class="il-client-name">{{
                    invoice.client ? invoice.client.name : "—"
                  }}</span>
                </b-col>
                <b-col md="1">
                  <span class="il-meta">{{ invoice.emission_date }}</span>
                </b-col>
                <b-col md="1" class="text-center">
                  <span class="il-meta">{{
                    invoice.products ? invoice.products.length : 0
                  }}</span>
                </b-col>
                <b-col md="1" class="text-right">
                  <span class="il-amount"
                    ><FormatAmount :amount="invoice.sub_total"
                  /></span>
                </b-col>
                <b-col md="1" class="text-right">
                  <span class="il-amount"
                    ><FormatAmount :amount="invoice.iva_total"
                  /></span>
                </b-col>
                <b-col md="2" class="text-right">
                  <span class="il-amount il-amount--accent"
                    ><FormatAmount :amount="invoice.total"
                  /></span>
                </b-col>
              </b-row>
            </div>
          </div>
        </paginate>
      </div>

      <!-- ── MOBILE: tarjetas ── -->
      <div class="il-cards d-md-none" key="il-mobile">
        <paginate
          name="resultQueryInvoices"
          :list="resultQueryInvoices"
          :per="perPaginate"
          :key="'m-' + searchQuery"
        >
          <div
            class="il-card"
            v-for="(invoice, index) in paginated('resultQueryInvoices')"
            :key="index"
            @click="openPreview(invoice)"
          >
            <!-- Cabecera de la tarjeta -->
            <div class="il-card__head">
              <span class="il-badge-code">{{ invoice.number_invoice }}</span>
              <span class="il-card__date">{{ invoice.emission_date }}</span>
            </div>

            <!-- Cliente -->
            <div class="il-card__client">
              <span class="il-card__client-name">{{
                invoice.client ? invoice.client.name : "—"
              }}</span>
              <span class="il-card__client-ruc">{{
                invoice.client ? invoice.client.document : "—"
              }}</span>
            </div>

            <!-- Montos -->
            <div class="il-card__amounts">
              <div class="il-card__amount-row">
                <span class="il-card__amount-label">Subtotal</span>
                <FormatAmount :amount="invoice.sub_total" />
              </div>
              <div class="il-card__amount-row">
                <span class="il-card__amount-label">IVA</span>
                <FormatAmount :amount="invoice.iva_total" />
              </div>
              <div class="il-card__amount-row il-card__amount-row--total">
                <span class="il-card__amount-label">Total</span>
                <FormatAmount :amount="invoice.total" class="il-text-accent" />
              </div>
            </div>

            <div class="il-card__footer">
              <span class="il-card__items">
                <i class="el-icon-goods"></i>
                {{ invoice.products ? invoice.products.length : 0 }} producto{{
                  (invoice.products ? invoice.products.length : 0) !== 1
                    ? "s"
                    : ""
                }}
              </span>
              <i class="el-icon-arrow-right il-card__arrow"></i>
            </div>
          </div>
        </paginate>
      </div>

      <!-- Paginación -->
      <paginate-links
        key="il-pagination"
        v-show="resultQueryInvoices.length > perPaginate"
        for="resultQueryInvoices"
        :classes="{ ul: 'pagination', li: 'page-item', a: 'page-link' }"
        :simple="{ next: 'Siguiente »', prev: '« Regresar' }"
      ></paginate-links>
    </template>

    <InvoicePreview
      key="il-preview"
      v-if="selectedInvoice"
      :invoice="selectedInvoice"
      :showModal="showPreview"
      :status="selectedInvoice ? selectedInvoice.status : ''"
      @close="closePreview"
    />
  </section>
</template>

<script>
import reportService from "../services/reportService";
import InputSearchComponent from "../../components/InputSearch";
import FilterBetweenDatesSelect from "../../components/FilterBetweenDatesSelect";
import FormatAmount from "@/components/General/FormatAmount";
import InvoicePreview from "../../sales/pages/invoices/components/InvoicePreview";
import invoiceService from "../../sales/pages/invoices/services/invoiceService";

export default {
  name: "InvoicesList",
  data() {
    return {
      paginate: ["resultQueryInvoices"],
      perPaginate: 12,
      invoices: {},
      searchDates: null,
      searchQuery: null,
      loading: false,
      selectedInvoice: null,
      showPreview: false,
      reqGen: 0
    };
  },
  computed: {
    resultQueryInvoices() {
      const raw = this.invoices?.invoices;
      if (!Array.isArray(raw) || !raw.length) return [];
      return raw.filter(item =>
        this.searchQuery
          ? (item.number_invoice || "")
              .toLowerCase()
              .includes(this.searchQuery.toLowerCase())
          : true
      );
    },
    summarySubtotal() {
      return this.resultQueryInvoices.reduce(
        (s, inv) => s + (parseFloat(inv.sub_total) || 0),
        0
      );
    },
    summaryIva() {
      return this.resultQueryInvoices.reduce(
        (s, inv) => s + (parseFloat(inv.iva_total) || 0),
        0
      );
    },
    summaryTotal() {
      return this.resultQueryInvoices.reduce(
        (s, inv) => s + (parseFloat(inv.total) || 0),
        0
      );
    },
    exportColumns() {
      return [
        { label: "Factura", value: i => i.number_invoice || "" },
        {
          label: "RUC / CUI",
          value: i =>
            i.client && i.client.document ? i.client.document + "001" : ""
        },
        { label: "Cliente", value: i => (i.client && i.client.name) || "" },
        { label: "Fecha", value: i => i.emission_date || "" },
        { label: "Items", value: i => (i.products ? i.products.length : 0) },
        { label: "Subtotal", value: i => parseFloat(i.sub_total) || 0 },
        { label: "IVA", value: i => parseFloat(i.iva_total) || 0 },
        { label: "Total", value: i => parseFloat(i.total) || 0 }
      ];
    }
  },
  methods: {
    async openPreview(invoice) {
      try {
        const res = await invoiceService.getInvoceId(invoice.id);
        if (res.data.success) {
          this.selectedInvoice = res.data.data;
        } else {
          this.selectedInvoice = invoice;
        }
      } catch {
        this.selectedInvoice = invoice;
      }
      this.showPreview = true;
    },
    closePreview() {
      this.showPreview = false;
      this.selectedInvoice = null;
    },
    onChangeSearch({ search }) {
      this.searchQuery = search;
    },
    onAsyncDataForDates({ searchDates }) {
      this.searchDates = searchDates;
      this.loading = true;
      this.invoices = {};
      this.getInvoicesList();
    },
    dateNow() {
      return new Date()
        .toISOString()
        .slice(0, 19)
        .replace(/-/g, "_")
        .replace("T", "_");
    },
    async sendReport() {
      this.loading = true;
      try {
        const response = await reportService
          .getSendReportInvoicesEmail({ params: { ...this.searchDates } })
          .finally(() => {
            this.loading = false;
          });

        const { success, data } = response.data;
        if (success) {
          const containerUrl = document.getElementById("url_download");
          const anchor = document.createElement("a");
          const filename = `${this.dateNow()}_descarga_reporte_facturas.pdf`;
          anchor.setAttribute("download", filename);
          anchor.setAttribute("href", data);
          anchor.innerHTML = filename;
          if (containerUrl.firstChild)
            containerUrl.removeChild(containerUrl.firstChild);
          containerUrl.appendChild(anchor);
          anchor.click();
          this.$notifications.success({
            message: "PDF descargado exitosamente"
          });
        }
      } catch (error) {
        return false;
      }
    },
    async getInvoicesList() {
      const gen = ++this.reqGen;
      try {
        this.loading = true;
        const response = await reportService.getDataInvoices({
          // No enviar business_id: getUser?.business_id no existe en el
          // perfil actual (queda undefined) y, si la sesión guardada en
          // localStorage es antigua, puede traer un id de negocio obsoleto
          // (p. ej. un user_business duplicado sin facturas) y el reporte
          // sale vacío sin ningún error. El backend ya resuelve el negocio
          // seleccionado del usuario cuando no se envía este parámetro.
          params: { ...this.searchDates },
          timeout: 120000
        });

        if (gen !== this.reqGen) return;
        if (response && response.data && response.data.success) {
          this.invoices = response.data.data;
        }
      } catch (error) {
        // silent — el interceptor ya notifica al usuario
      } finally {
        if (gen === this.reqGen) this.loading = false;
      }
    }
  },
  components: {
    InputSearchComponent,
    FilterBetweenDatesSelect,
    FormatAmount,
    InvoicePreview
  },
  mounted() {
    // Initial load handled by FilterBetweenDatesSelect -> onAsyncDataForDates
  }
};
</script>

<style lang="scss" scoped>
$primary: #2d2b55;
$accent: #3ecfb4;
$border: #eef0f8;
$radius: 12px;

.il-wrapper {
  padding: 4px 0;
}

/* ── KPI Summary ── */
.il-summary {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }

  &__card {
    background: #fff;
    border: 1.5px solid $border;
    border-radius: $radius;
    padding: 16px 20px;
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  &__label {
    font-size: 11px;
    color: #aaa;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    font-weight: 600;
  }

  &__value {
    font-size: 22px;
    font-weight: 700;
    color: $primary;

    &--accent {
      color: $accent;
    }
  }

  &__skeleton {
    display: block;
    height: 28px;
    width: 80%;
    border-radius: 6px;
    background: linear-gradient(90deg, #eef0f8 25%, #e4e6f0 50%, #eef0f8 75%);
    background-size: 200% 100%;
    animation: il-shimmer 1.4s infinite;
  }
}

@keyframes il-shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

/* ── Toolbar — uses global .dir-toolbar from main.scss ── */

/* ── Paginate list reset ── */
.il-wrapper ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

/* ── Header row (desktop) ── */
.il-header {
  margin-bottom: 0;
}

/* ── Card rows (desktop) ── */
.il-card-row {
  border: 1px solid $border;
  border-radius: 10px;
  margin-bottom: 8px;
  cursor: pointer;
  transition: box-shadow 0.15s;

  &:hover {
    box-shadow: 0 2px 12px rgba(83, 74, 183, 0.08);
  }

  &__body {
    padding: 12px 16px;
  }
}

.il-invoice-num {
  font-size: 13px;
  font-weight: 700;
  color: $primary;
}

.il-client-name {
  font-size: 13px;
  font-weight: 600;
  color: $primary;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;
}

.il-meta {
  font-size: 12px;
  color: #999;
}

.il-amount {
  font-size: 13px;
  font-weight: 600;
  color: $primary;

  &--accent {
    color: $accent;
    font-weight: 700;
  }
}

/* ── Cards (mobile) ── */
.il-cards {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.il-card {
  background: #fff;
  border: 1.5px solid $border;
  border-radius: $radius;
  padding: 14px 16px;
  cursor: pointer;
  transition: box-shadow 0.15s;
  &:active {
    box-shadow: 0 2px 12px rgba($primary, 0.08);
  }

  &__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 8px;
  }

  &__date {
    font-size: 12px;
    color: #aaa;
  }

  &__client {
    display: flex;
    flex-direction: column;
    margin-bottom: 12px;
  }

  &__client-name {
    font-size: 14px;
    font-weight: 700;
    color: $primary;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__client-ruc {
    font-size: 11px;
    color: #aaa;
    margin-top: 2px;
    font-family: monospace;
  }

  &__amounts {
    background: #f7f8fb;
    border-radius: 8px;
    padding: 10px 12px;
    margin-bottom: 10px;
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  &__amount-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 13px;
    color: $primary;

    &--total {
      border-top: 1px solid $border;
      padding-top: 6px;
      margin-top: 2px;
      font-weight: 700;
    }
  }

  &__amount-label {
    font-size: 11px;
    color: #aaa;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  &__footer {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  &__items {
    font-size: 11px;
    color: #aaa;
    display: flex;
    align-items: center;
    gap: 4px;
  }

  &__arrow {
    font-size: 12px;
    color: #c0c4cc;
  }
}

/* ── Helpers ── */
.il-badge-code {
  display: inline-block;
  background: rgba($primary, 0.07);
  color: $primary;
  border-radius: 6px;
  padding: 2px 8px;
  font-size: 11px;
  font-weight: 600;
  font-family: monospace;
}

.il-text-mono {
  font-family: monospace;
  font-size: 12px;
  color: $primary;
}

/* ── Empty ── */
.il-empty {
  text-align: center;
  padding: 60px 20px;
  color: #aaa;
  i {
    font-size: 40px;
    color: #c0c4d0;
    display: block;
    margin-bottom: 12px;
  }
  p {
    font-size: 14px;
    margin: 0;
  }
}
</style>
